"use client";

/**
 * Channel-Talk-style landing screen — first thing a user sees when opening the
 * support widget. Instead of a single "ask us" CTA the user first picks what
 * the question is about: booking/product questions (incl. cancellation and
 * refunds) go to the partner, only site/payment/account problems reach the
 * platform chat.
 */

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { SupportPartnerInquiry } from "./SupportPartnerInquiry";

interface SupportHomeTabProps {
  authed: boolean;
  displayName: string | null;
  onStartChat: () => void;
  onClose: () => void;
}

export function SupportHomeTab({
  authed,
  displayName,
  onStartChat,
  onClose,
}: SupportHomeTabProps) {
  const t = useTranslations("support.home");
  // 탭을 옮기면 TabsContent 가 언마운트되므로 다시 홈에 오면 선택 화면부터.
  const [view, setView] = useState<"choose" | "partner">("choose");

  if (view === "partner") {
    return (
      <SupportPartnerInquiry
        authed={authed}
        onBack={() => setView("choose")}
        onStartChat={onStartChat}
        onClose={onClose}
      />
    );
  }

  const greeting = displayName
    ? t("greetingNamed", { name: displayName })
    : t("greetingAnonymous");

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto bg-gray-50 px-3 py-4">
      <div className="flex items-start gap-3 px-1">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white border border-gray-400/30">
          <Image
            src="/locarora_sq.png"
            alt=""
            width={36}
            height={36}
            className="h-10 w-10 object-cover"
            // 로컬 /public 자산 — 정적 CDN 원본 서빙(Vercel 이미지 최적화 과금 제외).
            unoptimized
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold text-gray-900">{greeting}</p>
          <p className="mt-0.5 text-[14px] text-gray-700">{t("chooseTitle")}</p>
        </div>
      </div>

      <TopicOption
        primary
        title={t("partnerTopicTitle")}
        body={t("partnerTopicBody")}
        who={t("partnerTopicWho")}
        onClick={() => setView("partner")}
      />
      <TopicOption
        title={t("platformTopicTitle")}
        body={t("platformTopicBody")}
        who={t("platformTopicWho")}
        onClick={onStartChat}
      />
    </div>
  );
}

function TopicOption({
  primary = false,
  title,
  body,
  who,
  onClick,
}: {
  primary?: boolean;
  title: string;
  body: string;
  who: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition active:scale-[0.99]",
        primary
          ? "border-orange-200 bg-orange-50 hover:bg-orange-100/70"
          : "border-gray-200 bg-white hover:bg-gray-50",
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-semibold text-gray-900">{title}</p>
        <p className="mt-1 text-[13px] leading-relaxed text-gray-600">{body}</p>
        <p
          className={cn(
            "mt-1.5 text-[12px] font-medium",
            primary ? "text-orange-600" : "text-gray-500",
          )}
        >
          {who}
        </p>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
    </button>
  );
}
